import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { stableAPI, publicAPI, favoriteAPI } from '@/services/api';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import ImageViewer from "@/components/ImageViewer";
import BackLink from "@/components/BackLink";
import LocationViewer from "@/components/LocationViewer";
import StickyInfoCard from "@/components/StickyInfoCard";

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
	iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function StableDetailPage() {
	const { id } = useParams();
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [ad, setAd] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isFav, setIsFav] = useState(false);
	const [pricing, setPricing] = useState({
		free_images_limit: 5, price_extra_image: 0.70,
		free_videos_limit: 1, price_extra_video: 3.00,
		max_video_size_mb: 50, price_featured_ad: 10.00,
		vat_percent: 23
	});

	const API_BASE = process.env.REACT_APP_BACKEND_URL;

	useDocumentMeta({
		title: ad ? `${ad.title} | Equi-Market` : 'Equi-Market',
		description: ad?.description?.slice(0, 200),
		image: ad?.images?.[0] ? (ad.images[0].startsWith('http') ? ad.images[0] : `${API_BASE}${ad.images[0]}`) : '',
		url: ad ? `${window.location.origin}/stables/${ad.slug || ad.id}` : '',
		price: ad?.price,
		currency: 'EUR',
		type: 'stable',
	});

	useEffect(() => {
		setLoading(true);
		publicAPI
			.settings()
			.then(r => setPricing(prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		stableAPI
			.get(id)
			.then((r) => {
				setAd(r.data)
				if (user) {
					favoriteAPI
						.check(r.data.id)
						.then(f => setIsFav(f.data.is_favorite))
						.catch(() => {});
				}
			})
			.catch(() => navigate('/stables'))
			.finally(() => setLoading(false));
	}, [id, navigate]);

	if (loading || !ad) {
		return <div className="pt-24 min-h-screen flex items-center justify-center text-[#666055]">Loading...</div>;
	}

	if (!ad) return null;

	const boardingTypes = ad.boarding_types || [];
	const amenities = ad.amenities || [];
	const hiddenImages = ad.hidden_images || [];
	const images = (ad.images || []).filter(url => !hiddenImages.includes(url)).map((url, i) => ({
		url,
		preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
		thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null
	}));
	const hasLocation = ad.location_lat && ad.location_lng;

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid={`stable-detail-${ad.id}`}>
			<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Breadcrumb */}
				<BackLink title={ad.title} uri="/stables" textKey='nav.stables' />

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					{/* Left: details */}
					<div className="lg:col-span-2 space-y-6">

						{/* Main Image */}
						<ImageViewer images={images} title={ad.title} is_featured={ad.is_featured} />

						{/* Description */}
						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
							<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
								{t('horse.description')}
							</h2>
							<p className="text-sm text-[#3a3a3a] leading-relaxed whitespace-pre-line">
								{ad.description}
							</p>
						</div>

						{boardingTypes.length > 0 && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">{t('stable.boarding_types')}</h2>
								<div className="flex flex-wrap gap-2">
									{boardingTypes.map(b => (
										<span key={b} className="text-xs bg-[#F2F0E9] text-[#1A3C34] px-3 py-1.5 rounded-sm border border-[#E6E2D6]" data-testid={`stable-boarding-${b}`}>
                      {t(`stable.boarding_opt.${b}`, b)}
                    </span>
									))}
								</div>
							</div>
						)}

						{amenities.length > 0 && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">{t('stable.amenities')}</h2>
								<div className="flex flex-wrap gap-2">
									{amenities.map(a => (
										<span key={a} className="text-xs bg-[#F2F0E9] text-[#1A3C34] px-3 py-1.5 rounded-sm border border-[#E6E2D6]">
                      {t(`stable.amenity_opt.${a}`, a)}
                    </span>
									))}
								</div>
							</div>
						)}

						{/* Map */ }
						{ hasLocation &&
							<LocationViewer title={ ad.title }
															location_name={ ad.location_name }
															location_lat={ ad.location_lat }
															location_lng={ ad.location_lng }/>
						}
					</div>

					{/* Right - Sticky Info */}
					<StickyInfoCard ad={ad}
													adType="stable"
													is_favorite={isFav}
													pricing={pricing} />

				</div>
			</div>
		</div>
	);
}